/**
 * Contact API Service
 * Service layer for vendor contact management API calls
 *
 * CU-1a masters flip: `list` + `create` gain a live-module leg (the
 * `masters` key, client.ts) riding the VendorContactsCollection query /
 * vendorContactAdd mutation. getById / update / toggle / setPrimary stay on
 * the mock seam pending the B2.7' completion pass (register-listed
 * residual). The live list paginates client-side with the mock's paginate()
 * semantics (the graph models contacts as a vendor-embedded list).
 */

import apiClient from './axios.config';
import { isLiveModule } from './client';
import { refuseUnobservableMastersWrite } from './mastersWriteReach';
import { GraphQLTransportError, gqlMutate, gqlRequest, throwOnUserErrors } from './graphql.client';
import {
  mastersOperations,
  type VendorContactAddResult,
  type VendorContactSetActiveResult,
  type VendorContactsCollectionResult,
} from './masters.operations';
import {
  mapPartyContactNode,
  paginateLegacy,
  toVendorContactAddInput,
} from './masters.gql-map';
import { VENDOR_ROUTES } from '@/constants/api.constants';
import type {
  ContactListResponse,
  ContactResponse,
  ContactDetailResponse,
  CreateContactRequest,
  UpdateContactRequest,
  ToggleContactStatusRequest,
  ToggleContactStatusResponse,
  SetPrimaryContactRequest,
  SetPrimaryContactResponse,
  ContactListParams,
} from '@/types/api/contact';

/**
 * Contact API service object
 */
export const contactApi = {
  /**
   * GET /api/vendors/:vendorId/contacts
   * Fetch list of contacts for a vendor
   * Automatically sorted by: isPrimary (desc), isActive (desc), contactId (asc)
   */
  list: async (
    vendorId: number,
    params?: ContactListParams
  ): Promise<ContactListResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlRequest<VendorContactsCollectionResult>(
        mastersOperations.vendorContactsCollection,
        { id: String(vendorId) }
      );
      if (!result.vendor) {
        throw new GraphQLTransportError({
          message: `Vendor ${vendorId} not found`,
          graphQLErrors: ['NOT_FOUND'],
          status: 404,
        });
      }
      return paginateLegacy(
        result.vendor.contacts.map(mapPartyContactNode),
        params?.page,
        params?.pageSize
      );
    }
    const response = await apiClient.get<ContactListResponse>(
      VENDOR_ROUTES.CONTACTS(vendorId),
      { params }
    );
    return response.data;
  },

  /**
   * GET /api/vendors/:vendorId/contacts/:contactId
   * Fetch a single contact by ID
   * Returns raw address without location merging
   */
  getById: async (
    vendorId: number,
    contactId: number
  ): Promise<ContactDetailResponse> => {
    const response = await apiClient.get<ContactDetailResponse>(
      `${VENDOR_ROUTES.CONTACTS(vendorId)}/${contactId}`
    );
    return response.data;
  },

  /**
   * POST /api/vendors/:vendorId/contacts
   * Create a new contact for a vendor
   */
  create: async (
    vendorId: number,
    data: Omit<CreateContactRequest, 'vendorId'>
  ): Promise<ContactResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlMutate<VendorContactAddResult>(
        mastersOperations.vendorContactAdd,
        { input: toVendorContactAddInput(vendorId, data) }
      );
      throwOnUserErrors(result.vendorContactAdd.userErrors, 'vendorContactAdd');
      if (!result.vendorContactAdd.contact) {
        throw new GraphQLTransportError({
          message: 'vendorContactAdd returned neither a contact nor userErrors',
          graphQLErrors: ['EMPTY_PAYLOAD'],
        });
      }
      return mapPartyContactNode(result.vendorContactAdd.contact);
    }
    const requestData: CreateContactRequest = {
      ...data,
      vendorId,
    };

    const response = await apiClient.post<ContactResponse>(
      VENDOR_ROUTES.CONTACTS(vendorId),
      requestData
    );
    return response.data;
  },

  /**
   * PUT /api/vendors/:vendorId/contacts/:contactId
   * Update an existing contact
   */
  update: async (
    vendorId: number,
    contactId: number,
    data: UpdateContactRequest
  ): Promise<ContactResponse> => {
    refuseUnobservableMastersWrite('contactApi.update');
    const response = await apiClient.put<ContactResponse>(
      `${VENDOR_ROUTES.CONTACTS(vendorId)}/${contactId}`,
      data
    );
    return response.data;
  },

  /**
   * PATCH /api/vendors/:vendorId/contacts/:contactId/toggle
   * Toggle contact active status
   *
   * LIVE (CU-1b masters activation) — `vendorContactSetActive`, the boolean
   * travelling. `masterDataContactApi.toggleVendorContactStatus` addresses
   * this SAME route and rides the SAME document; only the declared response
   * differs, so each facade projects the shared echo down to its own shape.
   *
   * `ToggleContactStatusResponse = ContactResponse` and the mock echoes the
   * whole contact, so this side serves the full projection unprojected.
   */
  toggleStatus: async (
    vendorId: number,
    contactId: number,
    isActive: boolean
  ): Promise<ToggleContactStatusResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlMutate<VendorContactSetActiveResult>(
        mastersOperations.vendorContactSetActive,
        { input: { vendorId: String(vendorId), contactId, isActive } }
      );
      throwOnUserErrors(
        result.vendorContactSetActive.userErrors,
        'vendorContactSetActive'
      );
      if (!result.vendorContactSetActive.contact) {
        throw new GraphQLTransportError({
          message: 'vendorContactSetActive returned neither a contact nor userErrors',
          graphQLErrors: ['EMPTY_PAYLOAD'],
        });
      }
      return mapPartyContactNode(result.vendorContactSetActive.contact);
    }
    const response = await apiClient.patch<ToggleContactStatusResponse>(
      `${VENDOR_ROUTES.CONTACTS(vendorId)}/${contactId}/toggle`,
      { isActive } as ToggleContactStatusRequest
    );
    return response.data;
  },

  /**
   * PATCH /api/vendors/:vendorId/contacts/:contactId/set-primary
   * Set contact as primary
   */
  setPrimary: async (
    vendorId: number,
    contactId: number,
    isPrimary: boolean
  ): Promise<SetPrimaryContactResponse> => {
    refuseUnobservableMastersWrite('contactApi.setPrimary');
    const response = await apiClient.patch<SetPrimaryContactResponse>(
      `${VENDOR_ROUTES.CONTACTS(vendorId)}/${contactId}/set-primary`,
      { isPrimary } as SetPrimaryContactRequest
    );
    return response.data;
  },
};

export default contactApi;
